import { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * ThemeContext — gestiona modo claro/oscuro.
 *
 * Prioridad de resolucion del tema inicial:
 *   1. localStorage 'theme' si el usuario ya eligio ('light' | 'dark')
 *   2. Preferencia del sistema operativo (prefers-color-scheme)
 *   3. Fallback: 'dark'
 *
 * El toggle:
 *   - Si el usuario aun no eligio (modo 'system'), pasa a forzar el modo opuesto.
 *   - Si ya tenia un modo elegido, alterna entre light/dark.
 *
 * Exponemos:
 *   - `theme`: 'light' | 'dark' — el tema actualmente aplicado
 *   - `mode`: 'light' | 'dark' | 'system' — la PREFERENCIA del usuario
 *   - `toggle()`: invierte el tema actual
 *   - `setMode(m)`: fuerza 'light' | 'dark' | 'system'
 */
const ThemeContext = createContext(null);

const STORAGE_KEY = 'theme';

const getSystemTheme = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const applyClass = (theme) => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
};

export const ThemeProvider = ({ children }) => {
    // Modo elegido por el usuario (persistido). 'system' = sin override.
    const [mode, setModeState] = useState(() => {
        const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
        if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
        return 'system';
    });

    // Tema efectivo: si mode='system', lee SO; sino usa mode.
    const [theme, setTheme] = useState(() => (mode === 'system' ? getSystemTheme() : mode));

    // Aplicar clase al <html> cuando theme cambia
    useEffect(() => {
        applyClass(theme);
    }, [theme]);

    // Escuchar cambios del SO solo si el usuario esta en 'system'
    useEffect(() => {
        if (mode !== 'system' || !window.matchMedia) return;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => setTheme(e.matches ? 'dark' : 'light');
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, [mode]);

    const setMode = useCallback((newMode) => {
        setModeState(newMode);
        try { localStorage.setItem(STORAGE_KEY, newMode); } catch (e) {}
        const effective = newMode === 'system' ? getSystemTheme() : newMode;
        setTheme(effective);
    }, []);

    const toggle = useCallback(() => {
        // Si esta en 'system', pasa a forzar el opuesto del actual
        const next = theme === 'dark' ? 'light' : 'dark';
        setMode(next);
    }, [theme, setMode]);

    return (
        <ThemeContext.Provider value={{ theme, mode, setMode, toggle }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useTheme debe usarse dentro de <ThemeProvider>');
    return ctx;
};

/**
 * Script para aplicar el tema ANTES de que React renderice
 * (evita flash de fondo claro al cargar la app en dark).
 * Insertar el output en public/index.html dentro de <head>.
 */
export const THEME_INIT_SCRIPT = `
(function() {
  try {
    var saved = localStorage.getItem('${STORAGE_KEY}');
    var theme = saved === 'light' || saved === 'dark'
      ? saved
      : (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    if (theme === 'dark') document.documentElement.classList.add('dark');
  } catch(e) {}
})();
`;
